import {
  Component,
  Input,
  Output,
  EventEmitter,
  ElementRef,
  ViewChild,
  ChangeDetectorRef,
  effect,
} from '@angular/core';
import { PatientSessionService } from '../patient-session.service';
import {
  PatientBehaviorAntecedentModel,
  PatientBehaviorConsequenceModel,
  PatientBehaviorDataModel,
  PatientBehaviorLocationModel,
  PatientBehaviorModel,
  PatientBehaviorResponseModel,
  PatientBehaviorSeverityModel,
  PatientBehaviorType,
} from '../../../core/api/models';
import { BaseBoxComponent } from '../base-box/base-box.component';
import { ABCDataPanelComponent } from '../abc-data-panel/abc-data-panel.component';
import { AlertService } from '../../../shared/services/alert.service';

@Component({
  selector: 'app-box-behavior-frequency',
  templateUrl: './box-behavior-frequency.component.html',
  styleUrls: ['../data-entry-feedback.css'],
  standalone: false,
})
export class BoxBehaviorFrequencyComponent extends BaseBoxComponent {
  @Input() text: string | null | undefined = '-';
  @Input() dayValue: number = 0;
  @Input() sessionValue: number = 0;
  @Input() isTopBox: boolean = true;
  @Input() isBottomBox: boolean = false;
  @Input() showDayValue: boolean = true;
  @Input() behaviorID: number = 0;
  @Input() behavior: PatientBehaviorModel | undefined = undefined;
  @Input() isPreview?: boolean;

  behaviorData: PatientBehaviorDataModel[] = [];
  isEditing: boolean = false;
  behaviorType: PatientBehaviorType = PatientBehaviorType.Frequency;

  @ViewChild('inputField') inputField!: ElementRef;

  @ViewChild('abcDataPanel') abcDataPanel!: ABCDataPanelComponent;

  addingBehaviorData: boolean = false;
  justRecorded: boolean = false;

  constructor(
    protected override cdRef: ChangeDetectorRef,
    private patientSessionService: PatientSessionService,
    private alertService: AlertService
  ) {
    super(cdRef);
    this.isBehaviorBox = true; // Mark this as a behavior box to skip repositioning
    effect(() => {
      this.patientSessionService.dataChanged();
      const behaviorData = this.patientSessionService.getBehaviorData(
        this.behaviorID
      );
      if (behaviorData && behaviorData.length > 0) {
        this.behaviorData = behaviorData.filter((b) => !b.DeletedDate);
        //remove duplicate behaviorData
        this.behaviorData = this.behaviorData.filter(
          (value, index, self) =>
            index ===
            self.findIndex((t) => t.RecordedDate === value.RecordedDate)
        );
        this.sessionValue = this.behaviorData.length;
      } else {
        this.sessionValue = 0;
      }
    });
  }

  // Bulk add fields
  bulkStart: string = '';
  bulkEnd: string = '';
  bulkOccurrences: number | null = null;
  hasABCData: boolean = false;

  setExtraBoxes(): void {
    const dropdowns = document.querySelectorAll('.p-overlay.p-component');
    //convert Element to ElementRef
    this.extraBoxes = Array.from(dropdowns).map(
      (dropdown) => new ElementRef(dropdown)
    );
    this.extraBoxes.push(this.extraBox);
  }

  override onClickOutside() {
    this.isEditing = false;
    super.onClickOutside();
  }

  toggleEdit(): void {
    this.isEditing = !this.isEditing;
    if (this.isEditing) {
      this.abcDataPanel.setLastEditingABCData();
    } else {
      this.abcDataPanel.clearEditingABCData();
    }
  }

  increaseSessionValue(): void {
    this.addSessionValue();
  }

  addSessionValue(): void {
    if (this.addingBehaviorData) {
      return;
    }
    this.addingBehaviorData = true;
    //get date with up to seconds, remove milisencods
    const recordedDate = new Date()
      .toISOString()
      .slice(0, 19)
      .replace('T', ' ');
    const dateToSave = new Date(recordedDate).fromUTCtoServerString();

    //check if the dateToSave is already in the targetData
    const isDateInTargetData = this.behaviorData.some(
      (t) => t.RecordedDate === dateToSave
    );
    if (isDateInTargetData) {
      this.addingBehaviorData = false;
      return;
    }

    let behaviorData: PatientBehaviorDataModel = {
      TextValue: '1',
      IntValue: 1,
      PatientSessionID: 0,
      PatientBehaviorID: this.behaviorID,
      RecordedDate: dateToSave,
    };
    this.patientSessionService.addBehaviorData(behaviorData);
    this.justRecorded = true;
    setTimeout(() => {
      this.justRecorded = false;
    }, 220);
    setTimeout(() => {
      this.addingBehaviorData = false;
    }, 1000);
  }

  clickedBox(): void {
    if (!this.isEditing) {
      this.increaseSessionValue();
    }
  }

  decreaseSessionValue(): void {
    if (this.sessionValue > 0) {
      this.deleteData();
    }
  }

  override doLongPress(): void {
    this.toggleEdit();
    if (this.inputField && this.inputField.nativeElement) {
      this.inputField.nativeElement.focus();
    }
    this.showExtra();

    setTimeout(() => {
      //scroll tabs to end
      const tabs = document.querySelectorAll('.abc-tabs');
      tabs.forEach((tab) => {
        tab.scrollLeft = tab.scrollWidth;
      });
    }, 100);
  }

  deleteData(): void {
    //get the last targetData that dont have ABC data
    const nonABCBehaviorData = this.behaviorData.filter(
      (b) => !(b.Notes != null && b.Notes != undefined) && !b.DeletedDate
    );
    if (nonABCBehaviorData.length > 0) {
      const lastBehaviorData =
        nonABCBehaviorData[nonABCBehaviorData.length - 1];
      lastBehaviorData.DeletedDate = new Date().toServerString();
      this.patientSessionService.saveBehaviorData(lastBehaviorData);
      return;
    }

    // if there is no nonABCBehaviorData, check if there is abcBehaviorData to display proper error message.
    const abcBehaviorData = this.behaviorData.filter(
      (b) => b.Notes != null && b.Notes != undefined
    );
    if (abcBehaviorData.length > 0) {
      // the ABC data should be deleted first in this case.
      this.alertService.error('Please delete the related ABC data first.');
    }
  }

  openBulk(): void {
    this.showExtra3();
  }

  addBulkFrequency(): void {
    if (
      !this.bulkStart ||
      !this.bulkEnd ||
      !this.bulkOccurrences ||
      this.bulkOccurrences < 1
    ) {
      this.alertService.error(
        'Please enter start, end, and a valid number of occurrences.'
      );
      return;
    }

    const start = new Date(this.bulkStart);
    const end = new Date(this.bulkEnd);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      this.alertService.error('Invalid start or end date/time.');
      return;
    }
    if (end <= start) {
      this.alertService.error('End must be after start.');
      return;
    }

    const occurrences = Math.floor(this.bulkOccurrences);
    const totalSeconds = Math.floor((end.getTime() - start.getTime()) / 1000);
    if (totalSeconds < occurrences) {
      this.alertService.error(
        'Total duration must be at least the number of occurrences in seconds.'
      );
      return;
    }

    const stepSeconds = Math.floor(totalSeconds / occurrences);
    const remainder = totalSeconds - stepSeconds * occurrences;

    let accumulatedSeconds = 0;
    for (let i = 0; i < occurrences; i++) {
      const extra = i < remainder ? 1 : 0;
      const recordDate = new Date(start.getTime() + accumulatedSeconds * 1000);
      const recordedDateServer = recordDate.fromUTCtoServerString();

      // prevent duplicates to the second
      if (
        this.behaviorData.some((b) => b.RecordedDate === recordedDateServer)
      ) {
        accumulatedSeconds += stepSeconds + extra;
        continue;
      }

      const behaviorData: PatientBehaviorDataModel = {
        TextValue: '1',
        IntValue: 1,
        PatientSessionID: 0,
        PatientBehaviorID: this.behaviorID,
        RecordedDate: recordedDateServer,
        Notes: this.hasABCData ? ' ' : null,
      };
      this.patientSessionService.addBehaviorData(behaviorData);
      accumulatedSeconds += stepSeconds + extra;
    }

    this.bulkStart = '';
    this.bulkEnd = '';
    this.bulkOccurrences = null;
    this.closeExtra3();
  }
}
